import React, { useState, useEffect, useRef, useCallback } from "react";
import { ChevronDown, ChevronUp, X, SlidersHorizontal } from "lucide-react";

export interface FilterValues {
  priceRange: { min: number | undefined; max: number | undefined };
  brands: string[];
  rating: number | null;
  inStock: boolean;
  onSale: boolean;
  freeShipping: boolean;
}

interface ProductFilterPanelProps {
  filters: FilterValues;
  onFilterChange: (filters: FilterValues) => void;
  availableBrands?: string[];
  priceMin?: number;
  priceMax?: number;
  onClearAll?: () => void;
  isMobile?: boolean;
  onClose?: () => void;
}

const ProductFilterPanel: React.FC<ProductFilterPanelProps> = ({
  filters,
  onFilterChange,
  availableBrands = [],
  priceMin = 0,
  priceMax = 1000,
  onClearAll,
  isMobile = false,
  onClose,
}) => {
  const [expandedSections, setExpandedSections] = useState<{
    price: boolean;
    brand: boolean;
    rating: boolean;
    availability: boolean;
  }>({
    price: true,
    brand: true,
    rating: true,
    availability: true,
  });

  // Slider hard limits
  const SLIDER_MIN = 0;
  const SLIDER_MAX_DEFAULT = 1000;

  // Use priceMax from props as the slider maximum (updates when category/filters change)
  const sliderMax = priceMax > 0 ? priceMax : SLIDER_MAX_DEFAULT;

  // User filter values (independent from slider limits)
  const [filterMin, setFilterMin] = useState(
    filters.priceRange.min ?? priceMin,
  );
  const [filterMax, setFilterMax] = useState(
    filters.priceRange.max ?? priceMax,
  );

  // Refs to track current values for z-index calculation
  const minValRef = useRef(filters.priceRange.min ?? priceMin);
  const maxValRef = useRef(filters.priceRange.max ?? priceMax);
  const rangeRef = useRef<HTMLDivElement>(null);

  // Convert to percentage for range bar positioning
  const getPercent = useCallback(
    (value: number) =>
      Math.round(((value - SLIDER_MIN) / (sliderMax - SLIDER_MIN)) * 100),
    [sliderMax],
  );

  // Update range bar when min value changes
  useEffect(() => {
    if (rangeRef.current) {
      const minPercent = getPercent(filterMin);
      const maxPercent = getPercent(maxValRef.current);
      rangeRef.current.style.left = `${minPercent}%`;
      rangeRef.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [filterMin, getPercent]);

  // Update range bar when max value changes
  useEffect(() => {
    if (rangeRef.current) {
      const minPercent = getPercent(minValRef.current);
      const maxPercent = getPercent(filterMax);
      rangeRef.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [filterMax, getPercent]);

  // Sync local filter state when parent filter changes OR when priceMax changes
  useEffect(() => {
    const effectiveMin = filters.priceRange.min ?? priceMin;
    const effectiveMax = filters.priceRange.max ?? priceMax;
    setFilterMin(effectiveMin);
    setFilterMax(effectiveMax);
    minValRef.current = effectiveMin;
    maxValRef.current = effectiveMax;
  }, [filters.priceRange.min, filters.priceRange.max, priceMin, priceMax]);

  // Reset filter max when category changes (priceMax updates)
  useEffect(() => {
    // If current filterMax exceeds the new priceMax, reset it
    if (filterMax > priceMax && priceMax > 0) {
      setFilterMax(priceMax);
      maxValRef.current = priceMax;
      onFilterChange({
        ...filters,
        priceRange: { min: Math.min(filterMin, priceMax), max: priceMax },
      });
    }
  }, [priceMax]);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Clamp utility for filter values
  const clamp = (val: number, min: number, max: number) =>
    Math.max(min, Math.min(val, max));

  // Update filter and notify parent
  const updateFilter = (min: number, max: number) => {
    const newMin = clamp(min, SLIDER_MIN, Math.min(max, sliderMax));
    const newMax = clamp(max, Math.max(min, SLIDER_MIN), sliderMax);

    setFilterMin(newMin);
    setFilterMax(newMax);
    minValRef.current = newMin;
    maxValRef.current = newMax;

    onFilterChange({
      ...filters,
      priceRange: { min: newMin, max: newMax },
    });
  };

  const handleBrandToggle = (brand: string) => {
    const newBrands = filters.brands.includes(brand)
      ? filters.brands.filter((b) => b !== brand)
      : [...filters.brands, brand];
    onFilterChange({ ...filters, brands: newBrands });
  };

  // const handleRatingChange = (rating: number | null) => {
  //   onFilterChange({ ...filters, rating });
  // };

  const handleAvailabilityToggle = (
    key: "inStock" | "onSale" | "freeShipping",
  ) => {
    onFilterChange({ ...filters, [key]: !filters[key] });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (
      filters.priceRange.min !== priceMin ||
      filters.priceRange.max !== priceMax
    )
      count++;
    if (filters.brands.length > 0) count += filters.brands.length;
    if (filters.rating !== null) count++;
    if (filters.inStock) count++;
    if (filters.onSale) count++;
    if (filters.freeShipping) count++;
    return count;
  };

  const activeCount = getActiveFilterCount();

  return (
    <div
      className={`bg-neutral-900 ${isMobile ? "h-full" : "rounded-xl shadow-lg border border-neutral-800"} overflow-hidden flex flex-col`}
    >
      {/* Header */}
      <div className="bg-linear-to-r from-primary-dark to-primary px-6 py-4 border-b border-neutral-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SlidersHorizontal className="w-5 h-5 text-black" />
            <h2 className="text-lg font-bold text-black">Filters</h2>
            {activeCount > 0 && (
              <span className="bg-primary text-black text-xs font-bold px-2 py-1 rounded-full">
                {activeCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeCount > 0 && (
              <button
                onClick={() => onClearAll && onClearAll()}
                className="text-xs text-neutral-400 hover:text-white font-semibold underline"
              >
                Clear All
              </button>
            )}
            {isMobile && onClose && (
              <button
                onClick={onClose}
                className="text-neutral-400 hover:text-white p-1"
                aria-label="Close filters"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter Sections */}
      <div
        className={`overflow-y-auto flex-1 ${isMobile ? "px-4" : "px-6"} py-4 space-y-4`}
      >
        {/* Price Range Filter */}
        <div className="border-b border-neutral-800 pb-4">
          <button
            onClick={() => toggleSection("price")}
            className="w-full flex items-center justify-between py-2 hover:text-primary transition-colors"
          >
            <span className="font-semibold text-white">Price Range</span>
            {expandedSections.price ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>

          {expandedSections.price && (
            <div className="mt-4 space-y-4">
              {/* Price Range Inputs */}
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-neutral-400 mb-1">
                    Min
                  </label>
                  <input
                    type="number"
                    min={SLIDER_MIN}
                    max={filterMax}
                    value={filterMin}
                    onChange={(e) => {
                      let val = Number(e.target.value);
                      if (isNaN(val)) val = SLIDER_MIN;
                      setFilterMin(val);
                    }}
                    onBlur={() => updateFilter(filterMin, filterMax)}
                    className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <span className="text-neutral-600 mt-5">-</span>
                <div className="flex-1">
                  <label className="block text-xs text-neutral-400 mb-1">
                    Max
                  </label>
                  <input
                    type="number"
                    min={filterMin}
                    max={sliderMax}
                    value={filterMax}
                    onChange={(e) => {
                      let val = Number(e.target.value);
                      if (isNaN(val)) val = sliderMax;
                      setFilterMax(val);
                    }}
                    onBlur={() => updateFilter(filterMin, filterMax)}
                    className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              {/* Price Range Slider */}
              <div className="px-2">
                <div className="relative pt-1 h-8 flex items-center">
                  {/* Track background */}
                  <div
                    className="absolute w-full h-2 bg-neutral-800 rounded-full"
                    style={{ zIndex: 1 }}
                  />

                  {/* Active range bar */}
                  <div
                    ref={rangeRef}
                    className="absolute h-2 bg-primary rounded-full"
                    style={{ zIndex: 2 }}
                  />

                  {/* Min Range Slider */}
                  <input
                    type="range"
                    min={SLIDER_MIN}
                    max={sliderMax}
                    value={filterMin}
                    step={1}
                    onChange={(e) => {
                      const val = Math.min(
                        Number(e.target.value),
                        filterMax - 1,
                      );
                      setFilterMin(val);
                      minValRef.current = val;
                    }}
                    onMouseUp={() => updateFilter(filterMin, filterMax)}
                    onTouchEnd={() => updateFilter(filterMin, filterMax)}
                    className="absolute w-full h-2 bg-transparent appearance-none pointer-events-none cursor-pointer
                      [&::-webkit-slider-runnable-track]:bg-transparent
                      [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 
                      [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary 
                      [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary
                      [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md
                      [&::-webkit-slider-thumb]:hover:scale-110 [&::-webkit-slider-thumb]:transition-transform
                      [&::-webkit-slider-thumb]:pointer-events-auto
                      [&::-moz-range-track]:bg-transparent
                      [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5
                      [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary 
                      [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-primary
                      [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:shadow-md
                      [&::-moz-range-thumb]:pointer-events-auto"
                    style={{ zIndex: filterMin > sliderMax - 100 ? 5 : 3 }}
                  />

                  {/* Max Range Slider */}
                  <input
                    type="range"
                    min={SLIDER_MIN}
                    max={sliderMax}
                    value={filterMax}
                    step={1}
                    onChange={(e) => {
                      const val = Math.max(
                        Number(e.target.value),
                        filterMin + 1,
                      );
                      setFilterMax(val);
                      maxValRef.current = val;
                    }}
                    onMouseUp={() => updateFilter(filterMin, filterMax)}
                    onTouchEnd={() => updateFilter(filterMin, filterMax)}
                    className="absolute w-full h-2 bg-transparent appearance-none pointer-events-none cursor-pointer
                      [&::-webkit-slider-runnable-track]:bg-transparent
                      [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 
                      [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary 
                      [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary
                      [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md
                      [&::-webkit-slider-thumb]:hover:scale-110 [&::-webkit-slider-thumb]:transition-transform
                      [&::-webkit-slider-thumb]:pointer-events-auto
                      [&::-moz-range-track]:bg-transparent
                      [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5
                      [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary 
                      [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-primary
                      [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:shadow-md
                      [&::-moz-range-thumb]:pointer-events-auto"
                    style={{ zIndex: 4 }}
                  />
                </div>
                <div className="flex justify-between text-xs text-neutral-400 mt-3">
                  <span>${filterMin}</span>
                  <span>${filterMax}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Brand Filter */}
        {availableBrands.length > 0 && (
          <div className="border-b border-neutral-800 pb-4">
            <button
              onClick={() => toggleSection("brand")}
              className="w-full flex items-center justify-between py-2 hover:text-primary-600 transition-colors"
            >
              <span className="font-semibold text-white">Brand</span>
              {expandedSections.brand ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>

            {expandedSections.brand && (
              <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                {availableBrands.map((brand) => (
                  <label
                    key={brand}
                    className="flex items-center gap-3 cursor-pointer hover:bg-neutral-800 p-2 rounded-lg transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={filters.brands.includes(brand)}
                      onChange={() => handleBrandToggle(brand)}
                      className="w-4 h-4 accent-primary cursor-pointer"
                    />
                    <span className="text-sm text-neutral-300">{brand}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Rating Filter */}
        <div className="border-b border-neutral-800 pb-4">
          <button
            onClick={() => toggleSection("rating")}
            className="w-full flex items-center justify-between py-2 hover:text-primary transition-colors"
          >
            <span className="font-semibold text-white">Rating</span>
            {expandedSections.rating ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>
          {expandedSections.rating && (
            <div className="mt-3 flex items-center justify-center">
              <span className="inline-flex items-center gap-2 text-neutral-400 text-sm italic px-3 py-1 rounded border border-dashed border-neutral-700 bg-primary/10">
                <svg
                  className="w-4 h-4 text-neutral-500"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 8v4l2 2m8-2a10 10 0 11-20 0 10 10 0 0120 0z"
                  />
                </svg>
                Rating filter coming soon
              </span>
            </div>
          )}
          {/* {expandedSections.rating && (
            <div className="mt-3 space-y-2">
              {[5, 4, 3, 2, 1].map((rating) => (
                <label
                  key={rating}
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                >
                  <input
                    type="radio"
                    name="rating"
                    checked={filters.rating === rating}
                    onChange={() => handleRatingChange(rating)}
                    className="w-4 h-4 accent-primary-600 cursor-pointer"
                  />
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-4 h-4 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.966a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.921-.755 1.688-1.54 1.118l-3.38-2.455a1 1 0 00-1.175 0l-3.38 2.455c-.784.57-1.838-.197-1.539-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.174 9.393c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.966z" />
                      </svg>
                    ))}
                    <span className="text-sm text-gray-600 ml-1">& up</span>
                  </div>
                </label>
              ))}
              {filters.rating !== null && (
                <button
                  onClick={() => handleRatingChange(null)}
                  className="text-xs text-primary-600 hover:text-primary-700 font-semibold ml-2"
                >
                  Clear rating filter
                </button>
              )}
            </div>
          )} */}
        </div>

        {/* Availability Filters */}
        <div className="pb-4">
          <button
            onClick={() => toggleSection("availability")}
            className="w-full flex items-center justify-between py-2 hover:text-primary transition-colors"
          >
            <span className="font-semibold text-white">Availability</span>
            {expandedSections.availability ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>

          {expandedSections.availability && (
            <div className="mt-3 space-y-2">
              {/* <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
                <input
                  type="checkbox"
                  checked={filters.inStock}
                  onChange={() => handleAvailabilityToggle("inStock")}
                  className="w-4 h-4 accent-primary-600 cursor-pointer"
                />
                <span className="text-sm text-gray-700">In Stock</span>
              </label> */}

              <label className="flex items-center gap-3 cursor-pointer hover:bg-neutral-800 p-2 rounded-lg transition-colors">
                <input
                  type="checkbox"
                  checked={filters.onSale}
                  onChange={() => handleAvailabilityToggle("onSale")}
                  className="w-4 h-4 accent-primary cursor-pointer"
                />
                <span className="text-sm text-neutral-300">On Sale</span>
              </label>

              {/* <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
                <input
                  type="checkbox"
                  checked={filters.freeShipping}
                  onChange={() => handleAvailabilityToggle("freeShipping")}
                  className="w-4 h-4 accent-primary-600 cursor-pointer"
                />
                <span className="text-sm text-gray-700">Free Shipping</span>
              </label> */}
            </div>
          )}
        </div>
      </div>

      {/* Apply Button for Mobile */}
      {isMobile && onClose && (
        <div className="p-4 border-t border-neutral-800 bg-neutral-900">
          <button
            onClick={onClose}
            className="w-full bg-linear-to-r from-primary-600 to-primary hover:from-primary-700 hover:to-primary-600 text-white py-3 rounded-xl font-bold text-base transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            Apply Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductFilterPanel;
